import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import Countdown from "react-countdown";
import { HiExclamation, HiFilm, HiInformationCircle } from "react-icons/hi";

import { ClipQuota } from "MVVM/Utilities/ClipLimits";
import {
  getPendingClipCount,
  reconcilePendingClipCreations,
} from "MVVM/Utilities/ClipLimits/pendingClipCreations";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";

const BLOG_POST_URL = "/blog/allstar-subscriptions";
const TOOLTIP_TEXT = "Clip limit resets monthly on your subscription date.";
const GRACE_PERIOD_END = new Date("2026-02-26T17:00:00.000Z"); // Noon EST

interface ClipQuotaBarProps {
  clipQuota?: ClipQuota;
  userEmail?: string;
  compact?: boolean;
  bare?: boolean;
}

export const ClipQuotaBar = ({
  clipQuota,
  userEmail,
  compact = false,
  bare = false,
}: ClipQuotaBarProps) => {
  const history = useHistory();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();

  useEffect(() => {
    if (!clipQuota) return;
    reconcilePendingClipCreations(clipQuota.used);
  }, [clipQuota?.used]);

  if (!showSubscriptionOverhaul) return null;
  if (!clipQuota) return null;

  const isUnlimited = clipQuota.limit === null;
  const used = clipQuota.used + getPendingClipCount();
  const limit = clipQuota.limit ?? 0;
  const percentage = isUnlimited ? 0 : limit > 0 ? (used / limit) * 100 : 0;
  const isFull = !isUnlimited && used >= limit;

  const isAllstarEmail =
    userEmail?.toLowerCase().endsWith("@allstar.gg") ?? false;
  const isGracePeriod = !isAllstarEmail && new Date() < GRACE_PERIOD_END;

  const handleUpgrade = () => {
    history.push("/upgrade");
  };

  if (compact) {
    /* Unlimited plans used to render nothing here, hiding the one number a
       subscriber might still want. Count on the top line like the metered row,
       then the plan stated on the secondary line that row uses for its reset
       countdown.

       No bar: it has no ceiling to fill, and BarFill turns alert red past 90%,
       so a full one read as "almost out" when it means the opposite. */
    if (isUnlimited) {
      return (
        <CompactContainer>
          <CompactInfo>
            <CompactLabel>
              <CompactCount $isFull={false}>{used}</CompactCount>
              <Text ml={1} style={{ display: "inline" }}>
                clips created this month
              </Text>
            </CompactLabel>
          </CompactInfo>
          <CountdownRow>
            <CountdownText>
              You have unlimited clip creation on your plan
            </CountdownText>
          </CountdownRow>
        </CompactContainer>
      );
    }

    return (
      <CompactContainer>
        <CompactInfo>
          <CompactLabel>
            <CompactCount $isFull={isFull}>
              {used} / {limit}
            </CompactCount>
            <Text ml={1} style={{ display: "inline" }}>
              clips used this month
            </Text>
            <AnimatedTooltip
              content={TOOLTIP_TEXT}
              position="top"
              maxWidth={340}
              link={{ href: BLOG_POST_URL, label: "Learn more →" }}
            >
              <InfoIcon style={{ cursor: "pointer" }}>
                <HiInformationCircle size={16} />
              </InfoIcon>
            </AnimatedTooltip>
          </CompactLabel>
          <UpgradeLink onClick={handleUpgrade}>Upgrade →</UpgradeLink>
        </CompactInfo>
        <CompactBarWrapper>
          <BarFill $percentage={percentage} />
        </CompactBarWrapper>
        {clipQuota.nextAvailableAt && (
          <CountdownRow>
            <Countdown
              date={new Date(clipQuota.nextAvailableAt)}
              renderer={({ days, hours, minutes, seconds }) => {
                const timeString = `${days > 0 ? `${days}d ` : ""}${
                  hours < 10 ? "0" : ""
                }${hours}:${minutes < 10 ? "0" : ""}${minutes}:${
                  seconds < 10 ? "0" : ""
                }${seconds}`;
                return (
                  <CountdownText>
                    Next clip becomes available in {timeString}
                  </CountdownText>
                );
              }}
            />
          </CountdownRow>
        )}
        {isGracePeriod && (
          <GraceNotice $compact>
            <HiExclamation size={14} />
            Clips created before Feb 26 won't count toward your limit.
          </GraceNotice>
        )}
      </CompactContainer>
    );
  }

  if (isUnlimited) {
    return (
      <Container $bare={bare}>
        <MainRow>
          <LeftSection>
            <IconWrapper $isFull={false}>
              <HiFilm size={20} />
            </IconWrapper>
            <TextContent>
              <MainText>
                <UnlimitedLabel>Unlimited clip creation</UnlimitedLabel>
              </MainText>
            </TextContent>
          </LeftSection>
        </MainRow>
      </Container>
    );
  }

  return (
    <Container $bare={bare}>
      <MainRow>
        <LeftSection>
          <IconWrapper $isFull={isFull}>
            <HiFilm size={20} />
          </IconWrapper>
          <TextContent>
            <MainText>
              <Count>{used}</Count>
              <Separator>/</Separator>
              <Limit>{limit}</Limit>
              <Label>clips used this month</Label>
              <AnimatedTooltip
                content={TOOLTIP_TEXT}
                position="bottom"
                maxWidth={240}
                link={{ href: BLOG_POST_URL, label: "Learn more →" }}
              >
                <InfoIcon>
                  <HiInformationCircle size={14} />
                </InfoIcon>
              </AnimatedTooltip>
            </MainText>
            {isFull && (
              <SubText $isFull>Clip limit reached — upgrade for more</SubText>
            )}
          </TextContent>
        </LeftSection>
        <RightSection>
          <BarSection>
            <BarWrapper>
              <BarFill $percentage={percentage} />
            </BarWrapper>
            <PercentText $percentage={percentage}>
              {Math.round(percentage)}%
            </PercentText>
          </BarSection>
          <Divider />
          <UpgradeLink onClick={handleUpgrade}>Upgrade →</UpgradeLink>
        </RightSection>
      </MainRow>
      {clipQuota.nextAvailableAt && (
        <CountdownRow>
          <Countdown
            date={new Date(clipQuota.nextAvailableAt)}
            renderer={({ days, hours, minutes, seconds }) => {
              const timeString = `${days > 0 ? `${days}d ` : ""}${
                hours < 10 ? "0" : ""
              }${hours}:${minutes < 10 ? "0" : ""}${minutes}:${
                seconds < 10 ? "0" : ""
              }${seconds}`;
              const clipsAvailable = Math.max(1, limit - used + 1);
              return (
                <CountdownText>
                  {clipsAvailable}{" "}
                  {clipsAvailable === 1 ? "clip becomes" : "clips become"}{" "}
                  available in {timeString}
                </CountdownText>
              );
            }}
          />
        </CountdownRow>
      )}
      {isGracePeriod && (
        <GraceNotice>
          <HiExclamation size={14} />
          Clips created before Feb 26 won't count toward your limit.
        </GraceNotice>
      )}
    </Container>
  );
};

const GraceNotice = styled(Flex)<{ $compact?: boolean }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.error};
  font-family: Barlow;
  font-size: 13px;
  font-style: ${({ $compact }) => ($compact ? "italic" : "normal")};
  font-weight: 600;
  gap: 6px;
`;

const InfoIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade400};
  margin-left: 6px;

  &:hover {
    color: ${({ theme }) => theme.colors.shade200};
  }
`;

const CountdownRow = styled(Flex)`
  align-items: center;
`;

const CountdownText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
`;

// Compact styles (for modals)
const CompactContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  padding: 12px 14px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CompactInfo = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const CompactLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 400;
`;

const CompactCount = styled.span<{ $isFull?: boolean }>`
  color: ${({ theme, $isFull }) =>
    $isFull ? theme.colors.alert : theme.colors.ice};
  font-weight: 600;
`;

const CompactBarWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 3px;
  height: 6px;
  overflow: hidden;
  width: 100%;
`;

// Full styles (for settings pages); $bare embeds it in a shared card (banner)
const Container = styled(Box)<{ $bare?: boolean }>`
  background: ${({ theme, $bare }) =>
    $bare ? "transparent" : theme.colors.navy};
  border: ${({ theme, $bare }) =>
    $bare ? "none" : `1px solid ${theme.colors.navy50}`};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: visible;
  padding: ${({ $bare }) => ($bare ? "0" : "24px")};
  position: relative;
  width: 100%;
`;

const MainRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const LeftSection = styled(Flex)`
  align-items: center;
  gap: 14px;
`;

const IconWrapper = styled(Box)<{ $isFull?: boolean }>`
  align-items: center;
  background: ${({ theme, $isFull }) =>
    $isFull ? theme.colors.alert + "20" : theme.colors.envy + "20"};
  border-radius: 10px;
  color: ${({ theme, $isFull }) =>
    $isFull ? theme.colors.alert : theme.colors.envy};
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  width: 40px;
`;

const TextContent = styled(Box)``;

const MainText = styled(Flex)`
  align-items: baseline;
  gap: 4px;
  margin-bottom: 2px;
`;

const Count = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
`;

const Separator = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 22px;
  font-weight: 400;
`;

const Limit = styled(Text)`
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 22px;
  font-weight: 700;
`;

const Label = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 400;
  margin-left: 6px;
`;

const UnlimitedLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 18px;
  font-weight: 600;
`;

const SubText = styled(Text)<{ $isFull?: boolean }>`
  color: ${({ theme, $isFull }) =>
    $isFull ? theme.colors.alert : theme.colors.shade300};
  font-family: Barlow;
  font-size: 14px;
`;

const RightSection = styled(Flex)`
  align-items: center;
  flex: 1;
  gap: 14px;
  justify-content: flex-end;
`;

const BarWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
  width: 220px;

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const BarFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 4px;
  height: 100%;
  transition:
    width 0.3s ease,
    background 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;

const BarSection = styled(Flex)`
  align-items: center;
  flex: none;
  gap: 8px;

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const PercentText = styled(Text)<{ $percentage?: number }>`
  color: ${({ theme, $percentage = 0 }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.shade200;
  }};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  width: 48px;
`;

const Divider = styled(Box)`
  background: ${({ theme }) => theme.colors.navy50};
  height: 20px;
  width: 1px;
`;

const UpgradeLink = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  transition: opacity 0.15s ease;
  white-space: nowrap;
  width: 92px;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;
